<template>
  <FormGroup :scope="scope" :starts-enabled="!isInitialValueBlank || null" :errors="errors" :customLabel="customLabel" :hide-label="hideLabel || null">
    <template #disabled="{inputId, inputName}">
      <input v-for="locale in locales" :key="`hidden_${inputName}[${locale}]`" type="hidden" :name="`${inputName}[${locale}]`" value="">
    </template>
    <template #default="{inputId, inputName, label}">
      <div class="d-flex flex-column" :class="{ 'is-invalid': anyErrors }">
        <ul class="nav nav-tabs position-relative border-0 z-3" role="tablist">
          <li
            v-for="(locale, i) in locales"
            :key="`${calculatedId(inputId)}_${locale}_tab`"
            class="nav-item"
            role="presentation"
          >
            <label
              class="nav-link px-3 py-1"
              style="cursor: pointer"
              tabindex="0"
              :for="`${calculatedId(inputId)}_${locale}`"
              :class="{
                active: i === 0,
                'tab-invalid': hasError(locale),
              }"
              :id="`${calculatedId(inputId)}_${locale}_tab`"
              data-bs-toggle="tab"
              :data-bs-target="`#${calculatedId(inputId)}_${locale}_pane`"
              role="tab"
              :aria-controls="`${calculatedId(inputId)}_${locale}_pane`"
              :aria-selected="i === 0 || null"
            >
              <span aria-hidden="true">{{ $t(`languages.${locale}`) }}</span>
              <span class="visually-hidden">
                {{ $t("backend.candidacy.forms.form.name_in_language", { name: label, language: $t(`languages.${locale}`) }) }}
              </span>
            </label>
          </li>
        </ul>
        <div class="tab-content">
          <div
            v-for="(locale, i) in locales"
            :key="`${calculatedId(inputId)}_${locale}_pane`"
            :class="{
              'tab-pane': true,
              show: i === 0,
              active: i === 0,
            }"
            :id="`${calculatedId(inputId)}_${locale}_pane`"
            role="tabpanel"
            :aria-labelledby="`${calculatedId(inputId)}_${locale}_tab`"
          >
            <input
                v-model="currentValue[locale]"
                type="text"
                class="form-control"
                :class="{
                  'rounded-start-top-0': i === 0,
                  'input-changed': wasChanged(locale),
                  'is-invalid': hasError(locale),
                }"
                :id="`${calculatedId(inputId)}_${locale}`"
                :placeholder="inputPlaceholder(label, locale)"
                :name="`${inputName}[${locale}]`"
            />
          </div>
        </div>
      </div>
    </template>
  </FormGroup>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import FormGroup from "@/components/backend/candidacy/form_builder/form_items/shared/FormGroup.vue";
import type { BuildableItemError, Locale, LocalString, PropType } from "@/types";

export default defineComponent({
  name: "LocaleStringField",
  components: { FormGroup },
  emits: ["update"],
  props: {
    initialValue: {
      type: Object as PropType<LocalString>,
      default: {}
    },
    scope: {
      type: Array as PropType<string[]>,
      required: true,
    },
    locales: {
      type: Array as PropType<Locale[]>,
      default: ["en"],
    },
    placeholder: {
      type: String,
      default: "",
    },
    errors: {
      type: Array as PropType<BuildableItemError[]>,
      default: [],
    },
    customLabel: {
      type: String,
      default: null,
    },
    customId: {
      type: String,
      default: null,
    },
    hideLabel: {
      type: Boolean,
      default: false,
    },
  },
  data: ({ initialValue, locales }) => ({
    currentValue: Object.fromEntries(locales.map((l: Locale) => [l, initialValue[l]])),
  }),
  methods: {
    wasChanged(locale: Locale) {
      return this.initialValue[locale] !== this.currentValue[locale];
    },
    hasError(locale: Locale) {
      return this.errors.some((error: BuildableItemError) => error.path?.includes(locale));
    },
    inputPlaceholder(label: string, locale: Locale) {
      return this.placeholder || this.$t("backend.candidacy.forms.form.name_in_language", { name: label, language: this.$t(`languages.${locale}`) })
    },
    calculatedId(defaultId: string) {
      return this.customId || defaultId;
    }
  },
  computed: {
    isInitialValueBlank() {
      return Object.values(this.initialValue).every((v: string) => !v);
    },
    anyErrors() {
      return this.errors.length > 0;
    },
  },
  watch: {
    currentValue: {
      handler: function () {
        this.$emit("update", this.currentValue);
      },
      deep: true,
    },
  },
  mounted() {
    this.$emit("update", this.currentValue);
  },
});
</script>

<style scoped>
.rounded-start-top-0 {
  border-start-start-radius: 0;
}

.tab-invalid, .tab-invalid.active {
  color: var(--bs-form-invalid-border-color);
}

.tab-invalid.active {
  border: var(--bs-form-invalid-border-color) solid 1px !important;
  border-bottom-color: transparent !important;
}

.tab-invalid:not(.active):hover {
  border: rgb(from var(--bs-form-invalid-border-color) r g b / 20%) solid 1px !important;
  border-bottom-width: 0 !important;
}
</style>
